import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { CatIconComponent } from '@shared/component/basic/cat-icon/cat-icon.component';
import { RmPipe } from '@shared/pipe/rm.pipe';
import {
  autoAllocate, sharePercents, unallocated, type AllocRow,
} from '@shared/utils/deposit-allocation.util';
import { round2 } from '@shared/utils/money';

export type { AllocRow } from '@shared/utils/deposit-allocation.util';

/** A row plus the display-only figures the template needs. */
interface AllocVm extends AllocRow {
  /** cost share, already rounded so the column reads 100% — "50%", or "<1%" for a sliver. */
  shareLabel: string;
}

/**
 * Divides ONE payment phase's ringgit across the vendors on the booking.
 *
 * Used by both editors that can set a split — the offering editor (a Full Package's template)
 * and the New Booking wizard (the booking's own snapshot) — so the two can't drift. It is
 * deliberately pure and presentational: no SDK, no loader, no PersonaService. Both callers
 * resolve `costShare` themselves, from PARTNER COST rather than customer price, and hand the
 * rows in.
 *
 * Auto mode re-derives every untouched row so the column sums exactly to the phase. Typing in a
 * cell switches THAT row to manual; the rest stay auto and re-absorb the remainder. Over-
 * allocation ACROSS phases is the parent's job — only the parent knows the other phases — so
 * this component reports upward through `rowsChange` and shows only its own phase's footer.
 */
@Component({
  selector: 'app-deposit-allocation',
  standalone: true,
  imports: [CommonModule, CatIconComponent, RmPipe],
  templateUrl: './deposit-allocation.component.html',
  styleUrl: './deposit-allocation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepositAllocationComponent {
  private readonly _rows = signal<AllocRow[]>([]);
  /** Literal text per cell while it is being typed in, so a box can be genuinely empty. */
  private readonly draft = signal<Record<string, string>>({});
  private readonly _phaseAmount = signal(0);
  private readonly _packageTotal = signal(0);

  @Input() phaseLabel = '';

  /** The RM this phase collects — the ceiling every row fits inside. */
  @Input() set phaseAmount(v: number) { this._phaseAmount.set(v || 0); }
  get phaseAmount(): number { return this._phaseAmount(); }

  /**
   * The whole package price the phases add up to.
   *
   * Auto needs it to know what fraction of each vendor's COST this phase should pay. Without it
   * the grid can only share the phase out pro-rata, which pays the vendors the reseller's margin
   * as well as their costs. Zero keeps that legacy behaviour.
   */
  @Input() set packageTotal(v: number) { this._packageTotal.set(v || 0); }
  get packageTotal(): number { return this._packageTotal(); }

  @Input() set rows(v: AllocRow[]) { this._rows.set(v ?? []); }
  get rows(): AllocRow[] { return this._rows(); }

  @Input() disabled = false;

  @Output() rowsChange = new EventEmitter<AllocRow[]>();
  @Output() modeChange = new EventEmitter<'auto' | 'manual'>();

  /** The grid is in manual mode as soon as ANY row has been typed into. */
  readonly mode = computed<'auto' | 'manual'>(() =>
    this._rows().some(r => !r.isAuto) ? 'manual' : 'auto');

  readonly vm = computed<AllocVm[]>(() => {
    const rows = this._rows();
    // Rounded together, not one at a time — see `sharePercents`. Rounding each row on its own
    // showed a column of shares totalling 99% under a column of ringgit totalling exactly.
    const pcts = sharePercents(rows.map(r => r.costShare || 0));
    return rows.map((r, i) => ({
      ...r,
      // A real cost that rounds below 1% is "<1%", never "0%": zero next to an allocation of
      // RM 32.61 reads as broken. A genuinely costless row still shows 0%.
      shareLabel: (pcts[i] === 0 && (r.costShare || 0) > 0) ? '<1%' : `${pcts[i]}%`,
    }));
  });

  /** What the reseller keeps — negative once they allocate past what the phase collects. */
  readonly keep = computed(() => unallocated(this._rows(), this._phaseAmount()));

  readonly allocated = computed(() =>
    round2(this._rows().reduce((s, r) => s + (r.amount || 0), 0)));

  readonly over = computed(() => this.keep() < -0.004);

  /**
   * Money left on the table in MANUAL mode.
   *
   * The phase must be handed out in full: every ringgit the customer pays into it is earmarked for
   * a vendor, and a reseller keeping part of a payment inside the split is not what this grid is
   * for — their margin already sits on their own row (see `withOwnPieceCosts`). Auto always lands
   * on zero by construction, so this can only happen once somebody types.
   */
  readonly short = computed(() => this.mode() === 'manual' && this.keep() > 0.004);

  /** Either way the column doesn't balance — the footer turns red and says which way. */
  readonly unbalanced = computed(() => this.over() || this.short());

  trackByKey(_i: number, r: AllocVm): string { return r.key; }

  /** Auto: throw away every manual override and re-split the phase by cost share. */
  setAuto(): void {
    if (this.disabled) return;
    this.draft.set({});
    const next = autoAllocate(
      this._rows().map(r => ({ ...r, isAuto: true })), this._phaseAmount(), this._packageTotal());
    this.emit(next, 'auto');
  }

  /**
   * Manual: keep the figures currently on screen and let the reseller edit them. Every row is
   * pinned so the first keystroke in one cell doesn't send the others sliding.
   */
  setManual(): void {
    if (this.disabled) return;
    this.draft.set({});
    this.emit(this._rows().map(r => ({ ...r, isAuto: false })), 'manual');
  }

  /**
   * A typed cell. Only THAT row stops being auto; the remaining auto rows re-split what is left,
   * so giving the hall RM 2,000 of a RM 3,000 phase pushes RM 1,000 across everyone else rather
   * than silently unbalancing the column.
   */
  setAmount(key: string, raw: string): void {
    if (this.disabled) return;
    // Remember the literal keystrokes for THIS cell. Without it, clearing the box parsed to 0 and
    // `[value]` wrote that 0 straight back — so the field could never be emptied to type a new
    // figure, only overtyped.
    this.draft.update(m => ({ ...m, [key]: raw }));
    const v = raw.trim() === '' ? 0 : round2(Math.max(0, parseFloat(raw) || 0));
    const edited = this._rows().map(r => (r.key === key ? { ...r, amount: v, isAuto: false } : { ...r }));
    this.emit(autoAllocate(edited, this._phaseAmount(), this._packageTotal()), 'manual');
  }

  /**
   * Done with this cell — drop the draft so the field goes back to showing the model.
   * An empty box settles at 0, which is a real allocation ("give this vendor nothing here").
   */
  commitAmount(key: string): void {
    this.draft.update(m => {
      if (!(key in m)) return m;
      const next = { ...m };
      delete next[key];
      return next;
    });
  }

  /**
   * What the cell shows: the in-progress text while it is being edited, otherwise the model.
   *
   * A zero renders EMPTY rather than '0' so a cleared box stays visibly clear, and the parent
   * re-passing `rows` on every change-detection pass can't stomp what is being typed.
   */
  displayAmount(r: AllocVm): string {
    const d = this.draft()[r.key];
    if (d !== undefined) return d;
    return r.amount ? String(r.amount) : '';
  }

  private emit(next: AllocRow[], mode: 'auto' | 'manual'): void {
    this._rows.set(next);
    this.rowsChange.emit(next);
    this.modeChange.emit(mode);
  }
}
